import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { downloadDocument, prospectsDetails } from "../../services/merchant";
import { useAuth } from "../../context/AuthContextProvider";
import toast from "react-hot-toast";
import Spinner from "../../components/Spinner";
import ProfileNav from "../../components/ProfileNav";
import UpdateProspectModal from "./UpdateProspectModal";

const PendingMerchantDetails = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [details, setDetails] = useState(location.state?.prospect || null);
    const [loading, setLoading] = useState(!details);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);

    const handleDownload = async (file) => {
        try {
            const blobData = await downloadDocument(file.id);
            const url = window.URL.createObjectURL(new Blob([blobData]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", file.fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error("Failed to download document");
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center"><Spinner /></div>;
    if (!details) return <div className="h-screen flex items-center justify-center text-navy-blue font-bold">No details found</div>;

    return (
        <div className="h-screen p-4 overflow-y-auto">
            <div className="flex justify-between items-center py-4">
                <div className="flex items-center gap-2">
                    <svg
                        onClick={() => navigate(-1)}
                        className="cursor-pointer hover:opacity-70 transition-all"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M20 11H6.82998L9.70998 8.11998C10.1 7.72998 10.1 7.09998 9.70998 6.70998C9.31998 6.31998 8.68998 6.31998 8.29998 6.70998L3.70998 11.3C3.31998 11.69 3.31998 12.32 3.70998 12.71L8.29998 17.3C8.68998 17.69 9.31998 17.69 9.70998 17.3C10.1 16.91 10.1 16.28 9.70998 15.89L6.82998 13H20C20.55 13 21 12.55 21 12C21 11.45 20.55 11 20 11Z"
                            fill="#323232"
                        />
                    </svg>
                    <h1 className="text-navy-blue text-2xl font-bold">Pending Merchant Details</h1>
                </div>
                <ProfileNav />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mt-6 mb-10">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 border-b pb-6 mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-navy-blue">{details.merchantName || details.name}</h2>
                        <div className="flex items-center gap-3 mt-1">
                            <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                                Pending Review
                            </span>
                            <p className="text-gray-400 text-sm">#ONB-{details.onboardingId}</p>
                        </div>
                    </div>
                    <button
                        className="w-full sm:w-auto bg-primary text-white px-8 py-3 rounded-md font-bold shadow-lg hover:shadow-xl hover:bg-opacity-90 transform hover:-translate-y-0.5 transition-all active:scale-95"
                        onClick={() => setOpenUpdateModal(true)}
                    >
                        Update User
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
                    <DetailItem label="Business Name" value={details.merchantName} />
                    <DetailItem label="Contact Person" value={details.name} />
                    <DetailItem label="Email Address" value={details.email} />
                    <DetailItem label="Business Type" value={details.businessType === "001" ? "Generic Merchant" : "Payment Facilitator"} />
                    <DetailItem label="Phone Number" value={details.phoneNumber} />
                    <DetailItem label="Business Address" value={details.address} />
                    <DetailItem label="Approval Level" value={details.approvalLevel} />
                    <DetailItem label="Registration Date" value={details.created ? new Date(details.created).toLocaleString() : "N/A"} />
                    <DetailItem label="Merchant ID" value={details.merchantId} />
                    <DetailItem label="Terminal ID" value={details.terminalId} />
                    <DetailItem label="Web Address" value={details.businessWebAddress} isLink />
                    <DetailItem label="Test Web Address" value={details.testWebAddress} isLink />
                </div>

                <div className="mt-12">
                    <h3 className="text-xl font-bold text-navy-blue mb-6 flex items-center gap-2">
                        <span className="w-1.5 h-6 bg-orange-400 rounded-full"></span>
                        Bank Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8 bg-gray-50 p-6 rounded-xl border border-gray-100">
                        <DetailItem label="Bank Name" value={details.bankDetail?.bankName} />
                        <DetailItem label="Account Name" value={details.bankDetail?.accountName} />
                        <DetailItem label="Account Number" value={details.bankDetail?.accountNumber} />
                        <DetailItem label="BVN" value={details.bankDetail?.bvn} />
                    </div>
                </div>

                <div className="mt-12">
                    <h3 className="text-xl font-bold text-navy-blue mb-6 flex items-center gap-2">
                        <span className="w-1.5 h-6 bg-orange-400 rounded-full"></span>
                        Required Documents
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {details.documents && details.documents.length > 0 ? (
                            details.documents.map((doc) => (
                                <div key={doc.id} className="flex flex-col justify-between border border-gray-200 p-5 rounded-xl hover:border-primary hover:shadow-md transition-all group bg-white">
                                    <div className="flex items-start gap-3 mb-4">
                                        <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-primary/10 transition-colors">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#464255" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M14 2V8H20" stroke="#464255" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800 text-sm capitalize">{doc.key.replace(/-/g, ' ')}</p>
                                            <p className="text-xs text-gray-400 truncate max-w-[150px]">{doc.fileName}</p>
                                        </div>
                                    </div>
                                    <button
                                        className="w-full bg-gray-50 text-gray-700 py-2 rounded-lg text-sm font-bold hover:bg-primary hover:text-white transition-all border border-gray-100"
                                        onClick={() => handleDownload(doc)}
                                    >
                                        Download File
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full py-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center">
                                <p className="text-gray-400 font-medium italic text-sm">No documents uploaded for this prospect</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {openUpdateModal && (
                <UpdateProspectModal
                    prospect={details}
                    setOpenModal={setOpenUpdateModal}
                    onSuccess={() => {
                        setOpenUpdateModal(false);
                        navigate(-1);
                    }}
                    userEmail={user?.email || "Admin"}
                />
            )}
        </div>
    );
};

const DetailItem = ({ label, value, isLink }) => (
    <div className="flex flex-col gap-1.5 group">
        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{label}</span>
        {isLink && value ? (
            <a
                href={value.startsWith('http') ? value : `https://${value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[15px] font-bold text-primary hover:underline truncate"
            >
                {value}
            </a>
        ) : (
            <span className="text-[15px] font-bold text-navy-blue leading-tight">{value || <span className="text-gray-300 italic font-normal text-sm">Not provided</span>}</span>
        )}
    </div>
);

export default PendingMerchantDetails;
